import type {
  RawDeficiency,
  RawRecommendation,
} from "@/features/properties/components/propertyOverview/deficiencyHelpers";
import type { AuditSiteResponse } from "./types";

export interface NormalizedAudit {
  deficiencies: RawDeficiency[];
  recommendations: RawRecommendation[];
  score: number;
  recommendationTotal: number;
  narrativeHeadline: string | null;
}

/** Map the audit response onto the existing RawDeficiency/RawRecommendation shapes. */
export function normalizeAuditSite(res: AuditSiteResponse): NormalizedAudit {
  const deficiencies: RawDeficiency[] = (res.deficiencies ?? []).map((d) => ({
    id: d.id,
    rule_id: d.rule_id,
    severity: d.severity,
    group: d.group,
    title: d.title,
    detail: d.detail ?? null,
    recommendation_summary: d.recommendation_summary ?? null,
    asset: d.asset ?? null,
    survey_field_id: d.survey_field_id ?? null,
    explanation: d.explanation ?? null,
  }));
  const recommendations: RawRecommendation[] = (res.recommendations ?? []).map((r) => ({
    rule_id: r.rule_id ?? null,
    deficiency_rule_id: r.deficiency_rule_id ?? null,
    deficiency_asset_id: r.deficiency_asset_id ?? null,
    section: r.section ?? null,
    line_item: r.line_item ?? null,
    cost_estimate: r.cost_estimate ?? null,
    basis: r.basis ?? null,
    priority: r.priority ?? null,
  }));
  return {
    deficiencies,
    recommendations,
    score: res.score ?? 0,
    recommendationTotal: res.recommendation_total_estimate ?? 0,
    narrativeHeadline: res.narrative?.headline ?? null,
  };
}

/**
 * Stable composite key for a finding. `deficiency.id` is replaced on every
 * recompute, so React keys / dedup MUST use rule_id + the asset/survey anchor.
 */
export function deficiencyKey(
  d: Pick<RawDeficiency, "rule_id" | "asset" | "survey_field_id" | "id">,
): string {
  const rule = d.rule_id ?? "rule";
  const anchor = d.asset?.asset_id ?? d.survey_field_id ?? d.id ?? "?";
  return `${rule}::${anchor}`;
}

/** Recommendations that belong to a deficiency: match on rule_id + asset_id. */
export function recommendationsForDeficiency(
  d: RawDeficiency,
  recs: RawRecommendation[],
): RawRecommendation[] {
  const ruleId = d.rule_id ?? null;
  const assetId = d.asset?.asset_id ?? null;
  return recs.filter(
    (r) => (r.deficiency_rule_id ?? null) === ruleId && (r.deficiency_asset_id ?? null) === assetId,
  );
}
